import React from 'react'
import { ApplicationData } from '../SubmitDealApplication'

interface ReviewSubmissionFormProps {
  applicationData: ApplicationData
  onSubmit: () => void
  isSubmitting: boolean
}

const ReviewSubmissionForm: React.FC<ReviewSubmissionFormProps> = ({
  applicationData,
  onSubmit,
  isSubmitting
}) => {
  const formatCurrency = (value: string) => {
    if (!value) return 'Not provided'
    return `$${value}`
  }

  const formatFileInfo = (file: File | null) => {
    if (!file) return 'Not uploaded'
    return `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`
  }

  const formatFilesInfo = (files: File[]) => {
    if (files.length === 0) return 'No files uploaded'
    return files.map(file => `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`).join(', ')
  }

  const getValue = (value: string) => {
    return value || 'Not provided'
  }

  const formatFieldName = (fieldName: string): string => {
    // Convert camelCase to readable labels
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim()
  }

  const formatFieldValue = (fieldName: string, value: any): string => {
    if (!value) return 'Not provided'
    
    // Safety check: Don't render File objects or arrays of Files
    if (value instanceof File) {
      return `File: ${value.name}`
    }
    
    if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
      return `${value.length} file${value.length > 1 ? 's' : ''} uploaded`
    }
    
    // Safety check: Don't render complex objects
    if (typeof value === 'object' && value !== null) {
      return '[Complex data - not displayed]'
    }
    
    // Special formatting for different field types
    if (fieldName.toLowerCase().includes('date')) {
      try {
        return new Date(value).toLocaleDateString()
      } catch {
        return value
      }
    }
    
    if (fieldName.toLowerCase().includes('ssn')) {
      return '***-**-****' // Hide SSN for security
    }
    
    if (fieldName.toLowerCase().includes('revenue') || 
        fieldName.toLowerCase().includes('amount') || 
        fieldName.toLowerCase().includes('balance')) {
      return formatCurrency(value).replace('Not provided', value)
    }
    
    return String(value) // Ensure we always return a string
  }

  // Get only form fields (exclude file uploads and metadata)
  const getFormFields = () => {
    const excludeFields = [
      'applicationFiles', // Updated to match the current interface
      'bankStatements', 
      'parsedData', 
      'rawExtractedData', 
      'extractionMetadata',
      'translatedData',
      'flattenedForAutoFill'
    ]
    
    const formFields: { [key: string]: any } = {}
    Object.entries(applicationData).forEach(([key, value]) => {
      if (!excludeFields.includes(key) && value !== null && value !== undefined && value !== '') {
        formFields[key] = value
      }
    })
    
    return formFields
  }

  return (
    <div className="submit-deal-form-section">
      <div className="submit-deal-content-header">
        <h2 className="submit-deal-page-title">Review & Submit Application</h2>
      </div>

      <div className="submit-deal-review-section">
        <p className="submit-deal-section-description">
          Please review all the information below carefully before submitting your application. 
          You can go back to any previous section to make changes if needed.
        </p>

        {/* File Uploads Review */}
        <div className="submit-deal-review-group">
          <h3 className="submit-deal-review-title">
            <i className="bi bi-cloud-upload me-2"></i>
            Document Uploads
          </h3>
          
          <div className="submit-deal-review-item">
            <div className="submit-deal-review-label">Application Documents:</div>
            <div className="submit-deal-review-value">
              {applicationData.applicationFiles?.length > 0 ? (
                <>
                  {applicationData.applicationFiles.length} file{applicationData.applicationFiles.length > 1 ? 's' : ''} uploaded
                  <div className="mt-1 text-muted small">
                    {formatFilesInfo(applicationData.applicationFiles)}
                  </div>
                </>
              ) : (
                'No application files uploaded'
              )}
            </div>
          </div>
          
          <div className="submit-deal-review-item">
            <div className="submit-deal-review-label">Bank Statements:</div>
            <div className="submit-deal-review-value">
              {applicationData.bankStatements.length} files uploaded
              {applicationData.bankStatements.length > 0 && (
                <div className="mt-1 text-muted small">
                  {formatFilesInfo(applicationData.bankStatements)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic Form Fields Review */}
        <div className="submit-deal-review-group">
          <h3 className="submit-deal-review-title">
            <i className="bi bi-list-check me-2"></i>
            Application Details
          </h3>
          
          {Object.entries(getFormFields()).map(([fieldName, fieldValue]) => (
            <div key={fieldName} className="submit-deal-review-item">
              <div className="submit-deal-review-label">{formatFieldName(fieldName)}:</div>
              <div className="submit-deal-review-value">{formatFieldValue(fieldName, fieldValue)}</div>
            </div>
          ))}
          
          {Object.keys(getFormFields()).length === 0 && (
            <div className="text-muted">
              <i className="bi bi-info-circle me-2"></i>
              No application details have been filled out yet.
            </div>
          )}
        </div>

        {/* Terms and Conditions */}
        <div className="submit-deal-review-group">
          <h3 className="submit-deal-review-title">
            <i className="bi bi-shield-check me-2"></i>
            Terms and Conditions
          </h3>
          
          <div 
            style={{
              backgroundColor: '#f1f3f4',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb'
            }}
          >
            <h6 
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#02225b',
                marginBottom: '16px',
                textAlign: 'left'
              }}
            >
              By submitting this application, you acknowledge and agree to the following:
            </h6>
            
            <ul 
              className="mb-0" 
              style={{
                color: '#374151',
                fontSize: '14px',
                lineHeight: '1.6',
                paddingLeft: '20px'
              }}
            >
              <li style={{ marginBottom: '8px' }}>All information provided is true and accurate to the best of your knowledge</li>
              <li style={{ marginBottom: '8px' }}>You authorize credit checks and verification of the information provided</li>
              <li style={{ marginBottom: '8px' }}>You understand that submission does not guarantee approval</li>
              <li style={{ marginBottom: '8px' }}>Funding terms and conditions will be provided upon approval</li>
              <li style={{ marginBottom: '0' }}>You agree to our privacy policy and terms of service</li>
            </ul>
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            className="submit-deal-submit-button"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Submitting Application...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Submit Application
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReviewSubmissionForm
